// Файл simple_router.js

/**
 * Модуль обслуживает сценарий `simple_router` в разделе `utils`.
 * Он содержит обработчики и данные, относящиеся к этой части клиентского
 * интерфейса.
 * Код связывает локальную логику с соседними компонентами без
 * дублирования общих правил.
 * Такое разделение упрощает сопровождение, проверку поведения и
 * дальнейшее расширение.
 */

import React, {
    Children,
    createContext,
    isValidElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import LoadingIndicator from '../shared_code/loading';



const RouterContext = createContext(null);


const normalize_path = (path_value) => {
    const normalized_path = String(path_value || '/').split(/[?#]/, 1)[0];

    const with_leading_slash = normalized_path.startsWith('/')
        ? normalized_path
        : `/${normalized_path}`;

    if (with_leading_slash.length > 1) {
        return with_leading_slash.replace(/\/+$/, '');
    }

    return '/';
};


const current_location = () => ({
    pathname: normalize_path(window.location.pathname),
    search: window.location.search,
    hash: window.location.hash,
    state: window.history.state,
});


const BrowserRouter = ({ children }) => {
    const [location, set_location] = useState(current_location);

    useEffect(() => {
        const handle_location_change = () => {
            set_location(current_location());
        };

        window.addEventListener('popstate', handle_location_change);
        window.addEventListener('potale:navigation', handle_location_change);
        handle_location_change();

        return () => {
            window.removeEventListener('popstate', handle_location_change);
            window.removeEventListener(
                'potale:navigation',
                handle_location_change,
            );
        };
    }, []);

    const navigate = useCallback((target_path, options = {}) => {
        const normalized_path = normalize_path(target_path);
        const history_method = options.replace ? 'replaceState' : 'pushState';

        window.history[history_method](
            options.state || null,
            '',
            normalized_path,
        );

        // Обновляем state маршрутизатора напрямую. Иначе Navigate может
        // изменить URL раньше, чем BrowserRouter подпишется на событие,
        // и экран навсегда останется в состоянии ожидания.
        set_location(current_location());
        window.dispatchEvent(new Event('potale:navigation'));
    }, []);

    const context_value = useMemo(() => ({
        location,
        navigate,
    }), [location, navigate]);

    return (
        <RouterContext.Provider value={context_value}>
            {children}
        </RouterContext.Provider>
    );
};


const use_router_context = () => {
    const context_value = useContext(RouterContext);

    if (!context_value) {
        throw new Error('Маршрутизатор не подключён');
    }

    return context_value;
};


const useNavigate = () => use_router_context().navigate;
const useLocation = () => use_router_context().location;


const Route = () => null;


const route_matches = (route_path, current_path) => {
    if (route_path === '*') {
        return true;
    }

    return normalize_path(route_path) === normalize_path(current_path);
};


const Routes = ({ children }) => {
    const location = useLocation();
    const route_elements = Children.toArray(children).filter(isValidElement);
    const matched_route = route_elements.find((route_element) => (
        route_matches(route_element.props.path, location.pathname)
    ));

    return matched_route?.props.element || null;
};


const Navigate = ({ to, replace = false, state = null }) => {
    const navigate = useNavigate();

    useEffect(() => {
        navigate(to, { replace, state });
    }, [navigate, replace, state, to]);

    return <div className="route-loading">
                    <LoadingIndicator fullScreen />
                    <div className="route-loading__fallback">Загрузка...</div>
                </div>;
};


export {
    BrowserRouter,
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigate,
};
